import { Controller } from "@hotwired/stimulus";

export default class extends Controller {
  static targets = ["content", "button", "fade", "body"];
  static values = {
    collapsedClasses: { type: String, default: "max-h-64 overflow-hidden" },
    // fit mode: collapse to the space available and hide the toggle entirely when the content already fits.
    fit: { type: Boolean, default: false },
  };

  connect() {
    this.expanded = false;
    if (!this.fitValue) return;
    this.observer = new IntersectionObserver((entries) => {
      if (entries.some((entry) => entry.isIntersecting)) this.measure();
    });
    this.observer.observe(this.element);
  }

  measure() {
    if (!this.hasContentTarget || this.contentTarget.clientHeight === 0) return;
    this.observer.disconnect();
    if (this.hasButtonTarget) this.buttonTarget.classList.remove("hidden");

    this.collapsedHeight = Math.max(
      0,
      this.contentTarget.clientHeight + this.freeSpace() - 16, // small padding
    );
    this.collapsedClasses.forEach((className) => this.contentTarget.classList.remove(className));

    if (this.contentTarget.scrollHeight <= this.collapsedHeight + 1) {
      if (this.hasButtonTarget) this.buttonTarget.classList.add("hidden");
      this.setCollapsed(true);
    } else {
      this.setCollapsed(false);
    }
  }

  freeSpace() {
    const footer = this.hasBodyTarget ? this.bodyTarget.nextElementSibling : null;
    if (!footer) return 0;

    return Math.max(
      0,
      footer.getBoundingClientRect().top - this.bodyTarget.getBoundingClientRect().bottom,
    );
  }

  toggle() {
    this.setCollapsed(!this.expanded);
  }

  setCollapsed(expanded) {
    this.expanded = expanded;

    if (this.fitValue) {
      this.contentTarget.classList.toggle("overflow-hidden", !expanded);
      if (expanded) this.contentTarget.style.removeProperty("max-height");
      else this.contentTarget.style.maxHeight = `${this.collapsedHeight}px`;
    } else {
      this.collapsedClasses.forEach((className) => {
        this.contentTarget.classList.toggle(className, !expanded);
      });
    }

    if (this.hasFadeTarget) this.fadeTarget.classList.toggle("hidden", expanded);

    if (this.hasButtonTarget) {
      this.buttonTarget.textContent = expanded ? "Show Less" : "Show More";
      this.buttonTarget.setAttribute("aria-expanded", String(expanded));
    }
  }

  get collapsedClasses() {
    return this.collapsedClassesValue.split(/\s+/).filter(Boolean);
  }
}
